import React, { useEffect, useRef, useState } from "react";
import API from "../../../services/api";
import { formatDateWithTimeYear } from "../../../utils/dateFormat";
import { Toast } from "../../../shared/Toast";

const STATUS = [
  { value: -2, label: "Message", icon: "💬", color: "#000" },
  { value: 2, label: "Saved", icon: "💾", color: "#6B7280" },
  { value: 3, label: "Picked", icon: "📌", color: "#3baef6" },
  { value: 4, label: "Completed", icon: "✅", color: "#10b975" },
  { value: 5, label: "Rejected", icon: "❌", color: "#EF4444" },
  { value: 6, label: "Accepted", icon: "👍", color: "#190cea" },
  { value: 7, label: "Payment Completed", icon: "💵", color: "#800080" },
  { value: 8, label: "Live", icon: "🔥", color: "#fb542b" },
];

const Me = localStorage.getItem("name");

const statusMeta = (s) => STATUS.find((i) => i.value === s) || STATUS[0];
const initials = (n = "") =>
  n
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

const ChatContent = ({ data }) => {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(true);
  const endRef = useRef(null);
  const creative_id = data?.creative_id;

  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [messages]);

  const markAsRead = async() =>{
     try{
        const result = await API({
           url: "/alpha_cst_designer/mark_as_read",
           method: "POST",
           data: { creative_id },
        })
        data.unread_count=0;
     }catch(error){
        console.error("Error marking message as read:", error);
     }
  }

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await API({
        url: "/alpha_cst_designer/getactions",
        method: "POST",
        data: { creative_id },
      });
      setMessages(res.status === "SUCCESS" ? res.data : []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    creative_id && fetchMessages();
    creative_id && markAsRead();
  }, [creative_id]);

  const sendMessage = async () => {
    if (!text.trim()) return Toast("info", "Enter a message");
    const res = await API({
      url: "/alpha_cst_designer/postActions",
      method: "POST",
      data: { creative_id, creative_name: data.creative_name, message: text },
    });
    if (res.status === "SUCCESS") {
      setText("");
      fetchMessages();
    } else Toast("error", "Failed to send");
  };

  const Message = ({ msg, prev }) => {
    const me = msg.name === Me;
    const grouped = prev?.name === msg.name && formatDateWithTimeYear(prev.date) === formatDateWithTimeYear(msg.date);
    const s = statusMeta(msg.status);

    return (
      <div className={`flex ${me ? "justify-end" : "justify-start"} mt-${grouped ? "1" : "4"}`}>
        {!me && <div className="w-8 h-8 mr-2 flex-shrink-0 flex items-center justify-center text-xs font-bold">{!grouped && <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">{initials(msg.name)}</div>}</div>}

        <div className={`max-w-[70%] px-4 py-2 rounded-2xl shadow ${me ? "bg-blue-200" : "bg-white border"}`}>
          {!grouped && (
            <div className="flex justify-between gap-5 text-sm mb-1 opacity-70 font-semibold">
              <span>{me ? "You" : msg.name}</span>
              <span>{formatDateWithTimeYear(msg.date)}</span>
            </div>
          )}

          <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full mb-1" style={{ background: `${s.color}20`, color: s.color }}>
            {s.icon} {s.label}
          </span>

          {msg.message && <p className="text-sm">{msg.message}</p>}

          {msg.img_url &&
            (() => {
              const ext = msg.img_url.split(".").pop().toLowerCase();
              const isVideo = ["mp4", "webm", "ogg"].includes(ext);

              return (
                <div className="mt-2">
                  {isVideo ? (
                    <video src={msg.img_url} controls className="rounded-lg max-h-40 max-w-xs border" />
                  ) : (
                    <img src={msg.img_url} alt="attachment" onClick={() => window.open(msg.img_url, "_blank")} className="rounded-lg max-h-16 max-w-xs cursor-pointer border hover:opacity-90 object-cover" />
                  )}
                </div>
              );
            })()}
        </div>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-[calc(100vh-80px)]">
      {/* Header */}
      <div className="px-4 py-3 border-b bg-white">
        <h5 className="font-semibold">{data?.creative_name}</h5>
        <p className="text-xs text-gray-500">{messages.length} messages</p>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {loading ? <p className="text-center text-gray-400">Loading…</p> : messages.length ? messages.map((m, i) => <Message key={i} msg={m} prev={messages[i - 1]} />) : <p className="text-center text-gray-400 mt-10">No messages</p>}
        <div ref={endRef} />
      </div>

      {/* Input */}
      <div className="border-t bg-white p-3 flex gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          placeholder="Type a message…"
          className="flex-1 rounded-full border px-4 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none"
        />
        <button onClick={sendMessage} className="bg-blue-600 text-white px-5 flex gap-2 items-center justify-between rounded-full text-sm hover:bg-blue-700">
          <span>Send</span> <span>🚀</span>
        </button>
      </div>
    </div>
  );
};

export default ChatContent;
